/* Al Aswad: dated checkout estimates. No third-party scripts, polling, or fetch overrides. */
(() => {
  'use strict';
  const configNode = document.getElementById('AlAswadCheckoutDeliveryConfig');
  if (!configNode || window.AlAswadCheckoutDelivery) return;
  const config = JSON.parse(configNode.textContent);
  const root = String(config.root || '/').replace(/\/?$/, '/');
  const arabic = String(config.locale || document.documentElement.lang).startsWith('ar');
  const propertyKeys = ['Estimated delivery', 'موعد التسليم المتوقع'];
  const propertyKey = propertyKeys[arabic ? 1 : 0];
  const dayFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const dateFormatter = new Intl.DateTimeFormat(arabic ? 'ar-AE' : 'en-GB', {
    timeZone: 'UTC', calendar: 'gregory', year: 'numeric', month: 'short', day: 'numeric'
  });
  const isCustomValue = value => /^(custom|customize|customization|customised|customized|مخصص|تفصيل)$/i.test(String(value || '').trim());
  const productForms = 'form[action*="/cart/add"]';
  const cartAreas = 'm-cart, m-cart-drawer';
  const resumeForms = new WeakSet();
  const checkoutCarts = new WeakMap();
  let syncPromise, refreshTimer, checkoutBusy = false, revision = 0;
  let quantityPending = false, quantityWaiters = [];
  let cartVerified = false;

  function today() {
    const parts = dayFormatter.formatToParts(new Date());
    const get = key => Number(parts.find(part => part.type === key).value);
    return new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
  }

  function addDays(date, days) {
    const result = new Date(date);
    for (let count = 0; count < days;) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (result.getUTCDay() !== 0) count++;
    }
    return result;
  }

  function estimate(custom, base = today()) {
    const start = addDays(base, custom ? config.customMinDays : config.standardDays);
    const end = custom ? addDays(base, config.customMaxDays) : start;
    const dates = dateFormatter.format(start) + (custom ? ' – ' + dateFormatter.format(end) : '');
    return dates;
  }

  function customItem(item) {
    return isCustomValue(item.properties?.Sizing) ||
      (item.options_with_values || []).some(option => isCustomValue(option.value)) ||
      (item.variant_options || []).some(isCustomValue) ||
      String(item.variant_title || '').split(' / ').some(isCustomValue);
  }

  function desiredProperties(item, sharedCustom, base) {
    // Shopify replaces the whole properties object: retain EVERY other property.
    const properties = { ...(item.properties || {}) };
    propertyKeys.forEach(key => delete properties[key]);
    if (item.requires_shipping !== false) {
      properties[propertyKey] = estimate(config.shipTogether ? sharedCustom : customItem(item), base);
    }
    return properties;
  }

  function changedProperties(current, desired) {
    return propertyKeys.some(key => (current || {})[key] !== desired[key]);
  }

  function customForm(form) {
    const source = form.closest('sticky-atc')
      ? document.querySelector('.m-main-product--info .m-product-form--main form') || form : form;
    const sizing = source.querySelector('alaswad-sizing');
    const id = source.querySelector('[name="id"]')?.value;
    if (sizing && id) {
      try { return JSON.parse(sizing.querySelector('[data-custom-variants]').textContent).some(value => String(value) === String(id)); }
      catch (_) { /* Fall back to the sizing property on older forms. */ }
    }
    return isCustomValue(source.querySelector('[data-sizing-property]')?.value);
  }

  function prepareForm(form) {
    if (!form.matches(productForms)) return;
    let field = form.querySelector('[data-aa-delivery-property]');
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.dataset.aaDeliveryProperty = '';
      form.append(field);
    }
    field.name = 'properties[' + propertyKey + ']';
    field.value = estimate(customForm(form));
  }

  function prepareForms() { document.querySelectorAll(productForms).forEach(prepareForm); }

  async function request(path, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(root + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
      });
      const data = await response.json();
      if (!response.ok || data.errors || !Array.isArray(data.items)) throw new Error('Cart update failed');
      return data;
    } finally { clearTimeout(timer); }
  }

  function setPending(pending) {
    document.documentElement.toggleAttribute('data-aa-delivery-pending', pending);
    document.querySelectorAll('.additional-checkout-buttons, m-cart-items').forEach(element => {
      const locked = pending || (element.matches('.additional-checkout-buttons') && !cartVerified);
      if (locked && !element.inert) { element.inert = true; element.dataset.aaDeliveryLocked = ''; }
      if (!locked && element.hasAttribute('data-aa-delivery-locked')) {
        element.inert = false; delete element.dataset.aaDeliveryLocked;
      }
    });
  }

  function status(message, failed = false) {
    document.querySelectorAll('m-cart .m-cart__checkout, m-cart-drawer .m-cart__checkout, m-cart-drawer .m-cart-drawer__summary').forEach(area => {
      let node = area.querySelector('.aa-delivery-status');
      if (!node) {
        node = document.createElement('p');
        node.className = 'aa-delivery-status';
        node.setAttribute('aria-live', 'polite');
        area.append(node);
      }
      node.setAttribute('role', failed ? 'alert' : 'status');
      node.textContent = message;
      node.hidden = !message;
    });
  }

  function waitForQuantity() {
    if (!quantityPending) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const waiter = { resolve: () => { clearTimeout(timer); resolve(); } };
      const timer = setTimeout(() => {
        quantityWaiters = quantityWaiters.filter(item => item !== waiter);
        quantityPending = false;
        reject(new Error('A quantity update is still pending'));
      }, 10000);
      quantityWaiters.push(waiter);
    });
  }

  async function synchronize() {
    if (syncPromise) return syncPromise;
    syncPromise = (async () => {
      await waitForQuantity();
      setPending(true);
      let cart = await request('cart.js');
      let observedRevision = revision, changed = false;
      const limit = cart.items.length * 2 + 10;
      for (let pass = 0; pass < limit; pass++) {
        if (observedRevision !== revision || quantityPending) {
          await waitForQuantity();
          cart = await request('cart.js');
          observedRevision = revision;
        }
        const base = today();
        const sharedCustom = cart.items.some(item => item.requires_shipping !== false && customItem(item));
        const item = cart.items.find(line => changedProperties(line.properties, desiredProperties(line, sharedCustom, base)));
        if (!item) { cartVerified = true; return { cart, changed }; }
        // Use each NEW response's keys; property changes can change keys or merge lines.
        cart = await request('cart/change.js', {
          id: item.key, quantity: item.quantity, properties: desiredProperties(item, sharedCustom, base)
        });
        changed = true;
      }
      throw new Error('The cart changed during estimation');
    })();
    try { return await syncPromise; }
    finally { syncPromise = null; if (!checkoutBusy) setPending(false); }
  }

  function queueRefresh() {
    cartVerified = false;
    if (checkoutBusy) return;
    setPending(true);
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const result = await synchronize();
        status('');
        if (result.changed && !checkoutBusy) document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (_) {
        // The normal Checkout button retries and does not navigate with an unverified cart.
        status(arabic ? 'تعذّر تحديث موعد التسليم. يرجى استخدام زر إتمام الشراء للمحاولة مرة أخرى.' : 'We couldn’t refresh the delivery estimate. Please use Checkout to retry.');
      } finally { if (!checkoutBusy) setPending(false); }
    }, 120);
  }

  function cartChanged() {
    revision++;
    quantityPending = false;
    quantityWaiters.splice(0).forEach(waiter => waiter.resolve());
    queueRefresh();
  }

  function validTerms(form, button) {
    if (button?.disabled || button?.closest('[data-terms-action][disabled]')) return false;
    const terms = (form?.closest(cartAreas) || form)?.querySelector('[name="agree_terms"][required]');
    if (terms && !terms.checked) { terms.reportValidity(); return false; }
    return true;
  }

  async function checkout(form, button, url) {
    if (checkoutBusy || !validTerms(form, button)) return;
    checkoutBusy = true;
    cartVerified = false;
    clearTimeout(refreshTimer);
    setPending(true);
    if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    status(arabic ? 'جارٍ تحديث موعد التسليم…' : 'Updating your delivery estimate…');
    try {
      // Finish an existing background refresh, then read the current cart again.
      if (syncPromise) await syncPromise;
      const result = await synchronize();
      status('');
      if (button) button.disabled = false;
      if (url) { window.location.assign(url); return; }
      const currentForm = form.isConnected ? form : (form.id && document.getElementById(form.id)) || document.querySelector('m-cart form');
      if (!currentForm) throw new Error('Checkout form is unavailable');
      let submitter = button?.isConnected && button.form === currentForm ? button :
        Array.from(document.querySelectorAll('[name="checkout"]')).find(element => element.form === currentForm);
      if (!validTerms(currentForm, submitter)) throw new Error('Please confirm the cart terms');
      let temporary;
      if (!submitter) {
        temporary = document.createElement('button');
        temporary.type = 'submit'; temporary.name = 'checkout'; temporary.hidden = true;
        currentForm.append(temporary); submitter = temporary;
      }
      // Keep normal cart submission (notes, discounts, terms, and theme listeners).
      // Refresh quantity fields in the submission if old dated lines have merged.
      checkoutCarts.set(currentForm, result.cart);
      resumeForms.add(currentForm);
      try { currentForm.requestSubmit(submitter); }
      finally {
        resumeForms.delete(currentForm);
        checkoutCarts.delete(currentForm);
        temporary?.remove();
      }
    } catch (_) {
      status(arabic ? 'تعذّر تحديث موعد التسليم. يرجى المحاولة مرة أخرى.' : 'We couldn’t update the delivery estimate. Please try checkout again.', true);
    } finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
      checkoutBusy = false;
      setPending(false);
    }
  }

  document.addEventListener('formdata', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches(productForms)) {
      propertyKeys.forEach(key => event.formData.delete('properties[' + key + ']'));
      event.formData.set('properties[' + propertyKey + ']', estimate(customForm(form)));
    }
    const cart = checkoutCarts.get(form);
    if (cart) {
      for (const key of Array.from(event.formData.keys())) if (/^updates\[.*\]$/.test(key)) event.formData.delete(key);
      cart.items.forEach(item => event.formData.append('updates[]', String(item.quantity)));
    }
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches(productForms)) { prepareForm(form); return; }
    if (resumeForms.has(form)) return;
    if (!form.closest(cartAreas) || (event.submitter && event.submitter.name !== 'checkout')) return;
    if (!event.submitter && !form.querySelector('[name="checkout"]') && !document.querySelector('[form="' + form.id + '"][name="checkout"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(form, event.submitter);
  }, true);

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest('m-cart-remove-button')) {
      quantityPending = true; revision++; queueRefresh();
      return;
    }
    const link = target?.closest('a[href]');
    if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(null, null, url.href);
  }, true);

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element)) return;
    const form = event.target.closest(productForms);
    if (form) prepareForm(form);
    if (event.target.matches('[name="updates[]"]') && event.target.closest('m-cart-items')) {
      quantityPending = true; revision++; queueRefresh();
    }
  }, true);
  document.addEventListener('alaswad:sizing-change', event => {
    const form = event.target.closest('form'); if (form) prepareForm(form);
  });
  ['variant:changed', 'quick-view:loaded', 'shopify:section:load'].forEach(name => document.addEventListener(name, prepareForms));
  document.addEventListener('cart:updated', cartChanged);
  document.addEventListener('product-ajax:added', cartChanged);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      prepareForms();
      if (config.cartPage || document.querySelector('m-cart-drawer.m-cart-drawer--active')) queueRefresh();
    }
  });
  window.addEventListener('pageshow', event => { if (event.persisted) { prepareForms(); queueRefresh(); } });
  if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.openCartDrawer) {
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, queueRefresh);
  }
  prepareForms();
  if (config.cartPage) queueRefresh();
  window.AlAswadCheckoutDelivery = Object.freeze({ version: '1.0.1', refresh: queueRefresh });
})();

